import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { Button, Empty, Input, List, Select, Space, Spin, Typography } from "antd";
import AnnotationItem from "components/atoms/AnnotationItem";
import { useEffect, useMemo, useState } from "react";

const { Text } = Typography;

const serverFilterKeys = ["type", "state", "priority", "assignedTo", "sustained", "closed", "limit", "sortOrder"];

const AnnotationList = ({
    annotations = [],
    users = [],
    fields = [],
    onUpdate,
    onDelete,
    permission = {},
    serverFilters = {},
    defaultServerFilters = {},
    onServerFiltersChange,
    onServerFiltersReset,
    recordKey,
    isLoading = false,
    historyDisplayMode,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [targetFilter, setTargetFilter] = useState(undefined);
    const [filtersCollapsed, setFiltersCollapsed] = useState(true);

    useEffect(() => {
        setSearchTerm("");
        setTargetFilter(undefined);
        setFiltersCollapsed(true);
    }, [recordKey]);

    const handleServerFilterChange = (key, value) => {
        if (onServerFiltersChange) {
            onServerFiltersChange({ [key]: value });
        }
    };

    const resetFilters = () => {
        setSearchTerm("");
        setTargetFilter(undefined);
        if (onServerFiltersReset) {
            onServerFiltersReset();
        }
    };

    const hasServerFilters = useMemo(() => {
        return serverFilterKeys.some((key) => {
            const defaultValue = defaultServerFilters?.[key];
            const currentValue = serverFilters?.[key];
            return currentValue !== defaultValue;
        });
    }, [serverFilters, defaultServerFilters]);

    const hasActiveFilters = hasServerFilters || Boolean(searchTerm?.trim()) || Boolean(targetFilter);

    const sortedAnnotations = useMemo(() => {
        const items = [...annotations];
        const sortOrder = serverFilters?.sortOrder;

        if (sortOrder === "asc" || sortOrder === "desc") {
            return items;
        }

        return items.sort((a, b) => {
            const priorityOrder = { high: 3, medium: 2, low: 1 };
            const aPriority = priorityOrder[a.priority] || 0;
            const bPriority = priorityOrder[b.priority] || 0;
            if (aPriority !== bPriority) {
                return bPriority - aPriority;
            }

            if (a.sustained && !b.sustained) return -1;
            if (!a.sustained && b.sustained) return 1;

            return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
        });
    }, [annotations, serverFilters?.sortOrder]);

    const filteredAnnotations = useMemo(() => {
        const localSearch = searchTerm?.trim().toLowerCase();
        return sortedAnnotations.filter((annotation) => {
            if (localSearch) {
                const haystack =
                    `${annotation.content || ""} ${annotation.createdByName || ""} ${annotation.targetName || ""} ${annotation.closeComment || ""}`.toLowerCase() +
                    ` ${annotation.updatedByName || ""}`.toLowerCase();
                if (!haystack.includes(localSearch)) {
                    return false;
                }
            }

            if (targetFilter && annotation.targetName !== targetFilter) {
                return false;
            }

            return true;
        });
    }, [sortedAnnotations, searchTerm, targetFilter, serverFilters?.closed]);

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <div
                style={{
                    marginBottom: 16,
                    padding: "12px 16px",
                    background: "#f7f9fc",
                    border: "1px solid #e6ecf5",
                    borderRadius: 8,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: filtersCollapsed ? 0 : 12,
                        gap: 12,
                        flexWrap: "wrap",
                    }}
                >
                    <Text strong>Filter annotations</Text>
                    <Space size="small" wrap>
                        <Text type="secondary">
                            Showing {filteredAnnotations.length} of {annotations.length} annotations
                        </Text>
                        <Button type="link" size="small" onClick={resetFilters} disabled={!hasActiveFilters}>
                            Reset
                        </Button>
                        <Button type="link" size="small" onClick={() => setFiltersCollapsed((prev) => !prev)}>
                            {filtersCollapsed ? (
                                <>
                                    Show filters <DownOutlined />
                                </>
                            ) : (
                                <>
                                    Hide filters <UpOutlined />
                                </>
                            )}
                        </Button>
                    </Space>
                </div>
                {!filtersCollapsed && (
                    <>
                        <Space size={[8, 12]} wrap style={{ width: "100%" }}>
                            <Input.Search
                                allowClear
                                placeholder="Search text or @mentions"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ flex: "1 0 220px", minWidth: 200 }}
                            />
                            <Select
                                allowClear
                                placeholder="Type"
                                value={serverFilters?.type}
                                onChange={(value) => handleServerFilterChange("type", value)}
                                style={{ minWidth: 140 }}
                            >
                                <Select.Option value="issue">Issue</Select.Option>
                                <Select.Option value="review">Review</Select.Option>
                                <Select.Option value="info">Info</Select.Option>
                            </Select>
                            <Select
                                allowClear
                                placeholder="State"
                                value={serverFilters?.state}
                                onChange={(value) => handleServerFilterChange("state", value)}
                                style={{ minWidth: 156 }}
                            >
                                <Select.Option value="open">Open</Select.Option>
                                <Select.Option value="in_progress">In Progress</Select.Option>
                                <Select.Option value="resolved">Resolved</Select.Option>
                                <Select.Option value="dismissed">Dismissed</Select.Option>
                            </Select>
                            <Select
                                allowClear
                                placeholder="Priority"
                                value={serverFilters?.priority}
                                onChange={(value) => handleServerFilterChange("priority", value)}
                                style={{ minWidth: 140 }}
                            >
                                <Select.Option value="high">High</Select.Option>
                                <Select.Option value="medium">Medium</Select.Option>
                                <Select.Option value="low">Low</Select.Option>
                            </Select>
                            <Select
                                allowClear
                                placeholder="Sustained"
                                value={serverFilters?.sustained}
                                onChange={(value) => handleServerFilterChange("sustained", value)}
                                style={{ minWidth: 150 }}
                            >
                                <Select.Option value={true}>Sustained</Select.Option>
                                <Select.Option value={false}>Not sustained</Select.Option>
                            </Select>
                            <Select
                                allowClear
                                showSearch
                                placeholder="Assigned to"
                                value={serverFilters?.assignedTo}
                                onChange={(value) => handleServerFilterChange("assignedTo", value)}
                                style={{ minWidth: 180 }}
                                optionFilterProp="children"
                            >
                                {users
                                    .filter((user) => !user.deleted)
                                    .map((user) => (
                                        <Select.Option key={user.userId} value={user.userId} disabled={!user.active}>
                                            {user.firstName} {user.lastName}
                                            {!user.active ? " (inactive)" : ""}
                                        </Select.Option>
                                    ))}
                            </Select>
                            <Select
                                allowClear
                                showSearch
                                placeholder="Target field"
                                value={targetFilter}
                                onChange={(value) => setTargetFilter(value)}
                                style={{ minWidth: 180 }}
                                optionFilterProp="children"
                            >
                                {fields
                                    .filter((f) => f && f.name)
                                    .map((field) => (
                                        <Select.Option key={field.fieldId || field.name} value={field.name}>
                                            {field.name}
                                        </Select.Option>
                                    ))}
                            </Select>
                        </Space>
                        <Space size={[16, 8]} wrap style={{ marginTop: 12 }}>
                            <Select
                                allowClear
                                placeholder="Closed"
                                value={serverFilters?.closed}
                                onChange={(value) => handleServerFilterChange("closed", value)}
                                style={{ minWidth: 140 }}
                            >
                                <Select.Option value={false}>Open</Select.Option>
                                <Select.Option value={true}>Closed</Select.Option>
                            </Select>
                            <Select placeholder="Limit" value={serverFilters?.limit} onChange={(value) => handleServerFilterChange("limit", value)} style={{ minWidth: 120 }}>
                                <Select.Option value={10}>10 per page</Select.Option>
                                <Select.Option value={20}>20 per page</Select.Option>
                                <Select.Option value={50}>50 per page</Select.Option>
                            </Select>
                            <Select
                                placeholder="Sort order"
                                value={serverFilters?.sortOrder || "desc"}
                                onChange={(value) => handleServerFilterChange("sortOrder", value)}
                                style={{ minWidth: 150 }}
                            >
                                <Select.Option value="desc">Newest first</Select.Option>
                                <Select.Option value="asc">Oldest first</Select.Option>
                            </Select>
                        </Space>
                    </>
                )}
            </div>

            <div style={{ flex: "1 1 auto", overflow: "hidden" }}>
                <Spin spinning={isLoading} tip="Loading annotations..." style={{ height: "100%" }}>
                    <List
                        style={{ height: "100%", overflowY: "auto", paddingRight: "8px" }}
                        className="annotation-list"
                        dataSource={filteredAnnotations}
                        locale={{ emptyText: <Empty description="No annotations yet." /> }}
                        itemLayout="horizontal"
                        rowKey={(item) => item.annotationId}
                        renderItem={(item) => (
                            <AnnotationItem
                                annotation={item}
                                users={users}
                                fields={fields}
                                onUpdate={onUpdate}
                                onDelete={onDelete}
                                permission={permission}
                                historyDisplayMode={historyDisplayMode}
                            />
                        )}
                    />
                </Spin>
            </div>
        </div>
    );
};

export default AnnotationList;
